import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AppApiV2 } from '../../lib/api/app/AppApiV2';
import { checkFileSize, checkImageRatio, checkValidFileType } from '../../lib/utils/uploadFileCheck';
import { validateError } from '../../lib/utils/validateError';
import toastMsg from '../../lib/toastMsg';
import { ErrorResponseType, extractErrorMessage } from '../../lib/utils/checkErrorResponse';
import { IMAGE_ERROR_MESSAGE } from '../../constants/errorResponse/image';
import { IMAGE_MAX_SIZE } from '../../constants/service/common/commonConstants';

interface UseImageUpload {
  imageList: string[];
  setImageList: (imageList: string[]) => void;
  isModalOpen: boolean;
  cropModalToggle: () => void;
  uploadCroppedFile: (obj: File) => void;
  files: File | null;
  setFiles: React.Dispatch<React.SetStateAction<File | null>>;
  handleUploadFile: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  indexToUpdate: MutableRefObject<number | null>;
  handleDeleteImage: (index: number) => void;
}

interface IUseImageUploadProps {
  onChange?: (imageUrls: string[]) => void;
  value?: string[];
}

const useImageUpload = (props: IUseImageUploadProps): UseImageUpload => {
  const {
    onChange = () => {
      // empty
    },
    value = [],
  } = props;
  // todo: [윤성] 모달 오픈 로직 등을 제거해서 훅의 활용성 높이기 작업하기
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [files, setFiles] = useState<File | null>(null);
  const [imageList, setImageList] = useState<string[]>([]);
  const indexToUpdate = useRef<number | null>(null);

  const cropModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleUpdateImage = (index: number, newImage: string) => {
    const updatedImageList = imageList.map((previousImage: string, i: number) =>
      i === index ? newImage : previousImage,
    );
    setImageList(updatedImageList);
    onChange(updatedImageList);
  };

  const {
    isSuccess,
    mutate: imageUploadMutate,
    data,
  } = useMutation(async (payload: File) => AppApiV2.uploadsSquareImage(payload), {
    onError: (error) => {
      toastMsg.error(extractErrorMessage(error as ErrorResponseType));
    },
  });

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target && e.target.files) {
      const file = e.target.files[0];

      validateError(
        [
          {
            if: !file,
            onError: () => toastMsg.error(IMAGE_ERROR_MESSAGE.UPLOADIMAGE001),
          },
          {
            if: !checkFileSize(file, IMAGE_MAX_SIZE),
            onError: () => toastMsg.error(IMAGE_ERROR_MESSAGE.UPLOADIMAGE002),
          },
          {
            if: !checkValidFileType(file),
            onError: () => toastMsg.error(IMAGE_ERROR_MESSAGE.UPLOADIMAGE003),
          },
          {
            if: !(await checkImageRatio(file)),
            onError: () => {
              cropModalToggle();
              setFiles(file);
            },
          },
        ],
        () => {
          imageUploadMutate(file);
        },
      );
    }
    e.target.value = '';
  };

  const uploadCroppedFile = (obj: File) => {
    imageUploadMutate(obj);
    cropModalToggle();
  };

  const handleDeleteImage = (index: number) => {
    const updatedImageList = imageList.filter((_: string, i: number) => i !== index);
    setImageList(updatedImageList);
    onChange(updatedImageList);
  };

  useEffect(() => {
    if (isSuccess && data) {
      const { imageUrl } = data;
      if (indexToUpdate.current !== null) {
        handleUpdateImage(indexToUpdate.current, imageUrl);
        indexToUpdate.current = null;
      } else {
        setImageList([...imageList, imageUrl]);
        onChange([...imageList, imageUrl]);
      }
    }
  }, [isSuccess, data]);

  return {
    imageList,
    setImageList,
    isModalOpen,
    cropModalToggle,
    uploadCroppedFile,
    files,
    setFiles,
    handleUploadFile,
    handleDeleteImage,
    indexToUpdate,
  };
};

export default useImageUpload;
